import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { toast } from "sonner";
import { STATUS, TYPE_LABELS } from "./FeriePage";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Baby, Plus, X, CalendarIcon } from "lucide-react";

const CONGEDO_TYPES = {
  congedo_maternita: "Congedo maternità/paternità",
  congedo_parentale: "Congedo parentale",
  permesso_104: "Permesso Legge 104",
  lutto: "Congedo per lutto",
};

const fmt = (iso) => { try { return format(new Date(iso), "dd MMM yyyy", { locale: it }); } catch { return iso; } };

const DatePicker = ({ value, onChange, placeholder, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className="w-full flex items-center gap-2 border border-gray-300 rounded-sm px-3 py-2.5 text-[14px] text-left hover:border-[#16345c] transition-colors">
          <CalendarIcon size={16} className="text-gray-400" />
          {value ? format(value, "dd/MM/yyyy") : <span className="text-gray-400">{placeholder}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={(d) => { onChange(d); setOpen(false); }} locale={it} />
      </PopoverContent>
    </Popover>
  );
};

const CongediPage = () => {
  const [list, setList] = useState([]);
  const [type, setType] = useState("congedo_parentale");
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/leaves/mine").then((r) => setList(r.data.filter((l) => CONGEDO_TYPES[l.type]))).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!start || !end) { toast.error("Seleziona le date di inizio e fine"); return; }
    setSubmitting(true);
    try {
      await api.post("/leaves", {
        type, start_date: format(start, "yyyy-MM-dd"), end_date: format(end, "yyyy-MM-dd"),
        half_day: false, hours: null, reason,
      });
      toast.success("Richiesta di congedo inviata!");
      setStart(null); setEnd(null); setReason(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore nell'invio"); }
    finally { setSubmitting(false); }
  };

  const cancel = async (id) => {
    try { await api.put(`/leaves/${id}/cancel`); toast.success("Richiesta annullata"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="congedi-page">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><Baby size={24} /> Congedi e Maternità</h1>
        <p className="text-[14px] text-gray-500 mb-6">Congedi parentali, maternità/paternità, Legge 104 e permessi speciali.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-32">
              <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><Plus size={18} /> Nuova richiesta</h2>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Tipo di congedo</label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="w-full" data-testid="congedo-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CONGEDO_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Dal</label>
                  <DatePicker value={start} onChange={setStart} placeholder="Seleziona data" testid="congedo-start" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Al</label>
                  <DatePicker value={end} onChange={setEnd} placeholder="Seleziona data" testid="congedo-end" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Note (facoltative)</label>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="congedo-reason" placeholder="Dettagli della richiesta..." className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] resize-none" />
                </div>
                <button type="submit" disabled={submitting} data-testid="congedo-submit" className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold text-[14px] py-3 rounded-sm uppercase tracking-wide transition-colors disabled:opacity-60">
                  {submitting ? "Invio..." : "Invia richiesta"}
                </button>
              </form>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h2 className="text-[16px] font-bold text-[#16345c] mb-4">Le mie richieste di congedo</h2>
            {list.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
                <Baby size={40} className="mx-auto mb-3 opacity-40" /> Nessuna richiesta di congedo
              </div>
            ) : (
              <div className="space-y-3" data-testid="congedo-list">
                {list.map((lv) => (
                  <div key={lv.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[14px] font-bold text-[#16345c]">{TYPE_LABELS[lv.type] || lv.type}</span>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS[lv.status]?.cls}`}>{STATUS[lv.status]?.label}</span>
                      </div>
                      <div className="text-[13px] text-gray-600">{fmt(lv.start_date)} → {fmt(lv.end_date)} · {lv.days_count} gg</div>
                      {lv.reason && <div className="text-[12.5px] text-gray-400 mt-1 italic">"{lv.reason}"</div>}
                      {lv.decision_note && <div className="text-[12px] text-gray-500 mt-1"><b>{lv.approver_name}:</b> {lv.decision_note}</div>}
                    </div>
                    {lv.status === "in_attesa" && (
                      <button onClick={() => cancel(lv.id)} className="shrink-0 flex items-center gap-1 text-[12px] font-semibold text-[#c00510] hover:bg-red-50 px-2 py-1 rounded-sm transition-colors"><X size={14} /> Annulla</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CongediPage;
